import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAudio } from '../context/AudioContext';
import { jwtDecode } from 'jwt-decode';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  Skeleton,
  TextField,
  Container
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import GuitarIcon from '@mui/icons-material/MusicNote';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

const formatDuration = (seconds) => {
  if (!seconds) return '-:--';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const Playlist = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingSongs, setGeneratingSongs] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [generatedSong, setGeneratedSong] = useState(null);
  const intervalRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const nameInputRef = useRef(null);
  const [isCreator, setIsCreator] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { 
    currentSong, 
    isPlaying, 
    handlePlayPause, 
    playNext, 
    playPrevious, 
    setCurrentSong, 
    setIsPlaying,
    audioRef,
    setPlaylistAndCurrentSong
  } = useAudio();

  // Add a ref to store the latest playlist state
  const playlistRef = useRef(null);
  
  // Update the ref whenever playlist changes
  useEffect(() => {
    playlistRef.current = playlist;
  }, [playlist]);

  // Initial fetch when component mounts
  useEffect(() => {
    console.log('Initial fetch on mount');
    fetchPlaylist();
    
    // Cleanup function
    return () => {
      console.log('Component unmounting, clearing interval');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [id]);

  // Single polling effect that handles all polling logic
  useEffect(() => {
    console.log('Polling effect running, playlist:', playlist ? 'exists' : 'null');
    
    // Check if we need to start or stop polling
    const shouldPoll = playlist && playlist.songs && playlist.songs.some(song => song.isGenerating);
    console.log('Should poll:', shouldPoll);
    
    // If we should poll and there's no active interval, start polling
    if (shouldPoll && !intervalRef.current) {
      console.log('Starting polling interval');
      const interval = setInterval(async () => {
        console.log('Polling: fetching playlist');
        const updatedPlaylist = await fetchPlaylist();
        console.log('Polling: playlist fetched, has generating songs:', 
          updatedPlaylist && updatedPlaylist.songs && 
          updatedPlaylist.songs.some(song => song.isGenerating));
      }, 5000);
      
      intervalRef.current = interval;
      setGeneratingSongs(true);
    } 
    // If we shouldn't poll but there's an active interval, stop polling
    else if (!shouldPoll && intervalRef.current) {
      console.log('Stopping polling interval');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setGeneratingSongs(false);
    }
    else if (!shouldPoll) {
      console.log("setting generating songs to false because it seems there was a generation error")
      setGeneratingSongs(false);
    }
    
    // Cleanup function
    return () => {
      console.log('Polling effect cleanup');
      // Don't clear the interval here, let the next effect run handle it
    };
  }, [playlist]);

  // Add a new useEffect to check if the current song is in the playlist
  useEffect(() => {
    if (playlist && currentSong) {
      // Check if the current song is in this playlist
      const isCurrentSongInPlaylist = playlist.songs.some(song => 
        song._id === currentSong._id || song.audioUrl === currentSong.audioUrl
      );
      
      // If the current song is in this playlist and it's playing, update the UI
      if (isCurrentSongInPlaylist && isPlaying) {
        // The song is already playing, so we don't need to do anything
        // The UI will automatically show the pause button for this song
      }
    }
  }, [playlist, currentSong, isPlaying]);

  // Add effect to focus the input when editing starts
  useEffect(() => {
    if (isEditing && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditing]);

  // Add effect to initialize editedName when playlist changes
  useEffect(() => {
    if (playlist) {
      setEditedName(playlist.name);
    }
  }, [playlist]);

  // Add effect to check if playlist is saved
  useEffect(() => {
    const checkIfSaved = async () => {
      if (!playlist || isCreator) return;
      
      try {
        const token = localStorage.getItem('token');
        const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
        const response = await axios.get(`${baseUrl}/api/playlists/${id}/is-saved`, {
          headers: { 'x-auth-token': token }
        });
        setIsSaved(response.data.isSaved);
      } catch (error) {
        console.error('Error checking if playlist is saved:', error);
      }
    };
    
    checkIfSaved();
  }, [playlist, id, isCreator]);

  // Add effect to check authentication status
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);

    //load subscriptions status on page load from API endpoint
    const fetchSubscriptionStatus = async () => {
      if (!token) {
        localStorage.removeItem('hasSubscription');
        console.log('no token, removing hasSubscription from localStorage');
        return;
      }
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.get(`${baseUrl}/api/stripe/subscription-status`, {
        headers: { 'x-auth-token': token }
      });
      if(response.data.hasSubscription) {
        console.log('has subscription, setting hasSubscription in localStorage');
        localStorage.setItem('hasSubscription', response.data.hasSubscription);
      } else {
        console.log('no subscription, removing hasSubscription from localStorage');
        localStorage.removeItem('hasSubscription');
      }
    };
    fetchSubscriptionStatus();
  }, []);

  const fetchPlaylist = async () => {
    try {
      console.log('Fetching playlist:', id);
      const token = localStorage.getItem('token');
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.get(`${baseUrl}/api/playlists/${id}`, {
        headers: { 'x-auth-token': token }
      });
      console.log('response.data from fetchPlaylist', response.data);
      
      // Update playlist state
      setPlaylist(response.data);
      
      setLoading(false); // Set loading to false after successful fetch
      
      // Check if current user is the creator of the playlist
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          const currentUserId = decodedToken.userId;
          setIsCreator(response.data.user === currentUserId);
          console.log('isCreator', isCreator);
          console.log('currentUserId', currentUserId);
          console.log('response.data.user', response.data.user);
        } catch (error) {
          console.error('Error decoding token:', error);
          setIsCreator(false);
        }
      } else {
        setIsCreator(false);
      }
      
      // Update AudioContext if the current song is in this playlist
      console.log('got updated playlist. checking if current song (', currentSong, ') is in playlist');
      if (response.data && response.data.songs && currentSong) {
        const isCurrentSongInPlaylist = response.data.songs.some(song => 
          song._id === currentSong._id || song.audioUrl === currentSong.audioUrl
        );
        console.log('isCurrentSongInPlaylist', isCurrentSongInPlaylist);
        
        if (isCurrentSongInPlaylist) {
          // Find the updated version of the current song in the playlist
          const updatedCurrentSong = response.data.songs.find(song => 
            song._id === currentSong._id || song.audioUrl === currentSong.audioUrl
          );
          console.log('updatedCurrentSong', updatedCurrentSong);
          
          // Only update the AudioContext if the song is not already playing
          // or if the playlist ID is different
          // if (!isPlaying || (playlist && playlist._id !== response.data._id)) {
            // Update the AudioContext with the updated song and playlist
          setPlaylistAndCurrentSong(response.data.songs, updatedCurrentSong, response.data._id);
          console.log('updated playlist and current song', response.data.songs, updatedCurrentSong, response.data._id);
          // }
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching playlist:', error);
      setError('Failed to load playlist. Please try again later.');
      setLoading(false); // Set loading to false even if there's an error
      return null;
    }
  };


  // Add event listener for the even that plays first song in list if play is pressed on audio player
  useEffect(() => {
    // Add event listener for the custom event
    const handlePlayFirstAvailablePlaylistEvent = () => {
      handlePlayFirstAvailablePlaylist();
    };
    window.addEventListener('playFirstAvailablePlaylist', handlePlayFirstAvailablePlaylistEvent);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('playFirstAvailablePlaylist', handlePlayFirstAvailablePlaylistEvent);
    };
  }, [navigate]);


  // Update the handlePlayFirstAvailablePlaylist function to use the ref
  const handlePlayFirstAvailablePlaylist = () => {
    // Get the current playlist from the ref
    const currentPlaylist = playlistRef.current;
    console.log('handlePlayFirstAvailablePlaylist called with playlist:', currentPlaylist);
    
    // Check if playlist and playlist.songs exist
    if (currentPlaylist && currentPlaylist.songs && currentPlaylist.songs.length > 0) {
      console.log('Playing first user playlist:', currentPlaylist);
      setPlaylistAndCurrentSong(currentPlaylist.songs, currentPlaylist.songs[0], currentPlaylist._id);
      return;
    }
  };


  const handleGenerateSongs = async () => {
    setGeneratingSongs(true);
    setGenerationError(null);

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Check if user has an active subscription
    const hasSubscription = localStorage.getItem('hasSubscription');
    if (!hasSubscription) {
      navigate('/subscription');
      return;
    }

    try {
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.post(`${baseUrl}/api/playlists/${id}/generate`,
        { 
          playlistId: id,
          playlistStyle: playlist.playlistStyle,
          instrumental: playlist.instrumental
        },
        {
          headers: { 'x-auth-token': token }
        }
      );
      
      console.log('response.data from generate songs', response.data);
      if (response.data && response.data.playlist) {
        // Update the playlist with the complete updated playlist from the server
        setPlaylist(response.data.playlist);
      }
    } catch (err) {
      console.error('Error generating songs:', err);
      if (err.response?.status === 403) {
        setGenerationError(err.response.data?.message || 'You can only generate songs for playlists you created');
      } else {
        setGenerationError('Failed to generate songs. Please try again.');
      }
    } finally {
      setGeneratingSongs(false);
    }
  };

  const handlePlaySong = (song) => {
    console.log('handlePlaySong called with song:', song);
    console.log('currentSong', currentSong);

    // Check if this song is already the current song
    if (currentSong && currentSong._id === song._id) {
      // If it's the same song, just toggle play/pause
      handlePlayPause();
      return;
    }
    
    // Set the current song and update the playlist context
    // setCurrentSong(song);
    // setIsPlaying(true);
    
    // Update the playlist in the AudioContext with the playlist ID
    setPlaylistAndCurrentSong(playlist.songs, song, playlist._id);
  };

  const handleEditName = () => {
    setIsEditing(true);
  };

  const handleSaveName = async () => {
    try {
      if (!editedName.trim()) {
        setError("Playlist name cannot be empty");
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.put(`${baseUrl}/api/playlists/${id}`,
        { name: editedName },
        {
          headers: { 'x-auth-token': token }
        }
      );

      if (response.data) {
        setPlaylist(response.data);
        setIsEditing(false);
        setError(null);
      }
    } catch (error) {
      console.error('Error updating playlist name:', error);
      setError("Failed to update playlist name. Please try again.");
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedName(playlist ? playlist.name : '');
    setError(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      console.log('Enter key pressed');
      handleSaveName();
    } else if (e.key === 'Escape') {
      console.log('Escape key pressed');
      handleCancelEdit();
    }
  };

  const handleSavePlaylist = async () => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.post(`${baseUrl}/api/playlists/${id}/save`, {}, {
        headers: { 'x-auth-token': token }
      });
      
      setIsSaved(true);
      
      // Update the playlist with the data returned from the server
      if (response.data && response.data.playlist) {
        setPlaylist(response.data.playlist);
      }
    } catch (error) {
      console.error('Error saving playlist:', error);
    }
  };

  const handleUnsavePlaylist = async () => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = window.location.origin.replace('3000', '8000').replace('3500', '8500');
      const response = await axios.delete(`${baseUrl}/api/playlists/${id}/save`, {
        headers: { 'x-auth-token': token }
      });
      
      setIsSaved(false);
      
      // Update the playlist with the data returned from the server
      if (response.data && response.data.playlist) {
        setPlaylist(response.data.playlist);
      }
    } catch (error) {
      console.error('Error unsaving playlist:', error);
    }
  };

  if (!playlist) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      // ) : error ? (
      //   <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      ) : playlist ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {isEditing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <TextField
                  inputRef={nameInputRef}
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mr: 1 }}
                  error={!!error}
                  // helperText={error}
                />
                <IconButton 
                  color="primary" 
                  onClick={handleSaveName}
                  aria-label="save"
                >
                  <SaveIcon />
                </IconButton>
                <IconButton 
                  color="error" 
                  onClick={handleCancelEdit}
                  aria-label="cancel"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <>
                <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
                  {playlist.name}
                </Typography>
                {isAuthenticated && (
                  isCreator ? (
                    <IconButton 
                      color="primary" 
                      onClick={handleEditName}
                      aria-label="edit playlist name"
                    >
                      <EditIcon />
                    </IconButton>
                  ) : (
                    <Tooltip title={isSaved ? "Unsave playlist" : "Save playlist"}>
                      <IconButton 
                        onClick={() => isSaved ? handleUnsavePlaylist() : handleSavePlaylist()}
                        color={isSaved ? "primary" : "default"}
                        aria-label={isSaved ? "unsave playlist" : "save playlist"}
                      >
                        {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                      </IconButton>
                    </Tooltip>
                  )
                )}
              </>
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 0, mb: 6 }}>{error}</Alert>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="body1" color="text.secondary">
                Style: {playlist.playlistStyle}
              </Typography>
              {playlist.instrumental && (
                <Typography variant="body2" color="text.secondary">
                  Instrumental
                </Typography>
              )}
              {playlist.otherData?.savedCount > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Saved by {playlist.otherData.savedCount} {playlist.otherData.savedCount === 1 ? 'user' : 'users'}
                </Typography>
              )}
            </Box>
          
            {/* Playlist Actions */}
            <div className="flex flex-col gap-4 mt-4">
              {isAuthenticated && (
                <>
                  {isCreator ? (
                    <>
                      {localStorage.getItem('hasSubscription') ? (
                        <Button
                          variant="contained"
                          onClick={handleGenerateSongs}
                          disabled={generatingSongs}
                          sx={{
                            color: 'black',
                            textTransform: 'none',
                            borderRadius: '20px',
                            padding: { xs: '8px 8px', sm: '8px 24px' },
                            marginLeft: { xs: '8px', sm: '8px' },
                            '&:hover': {
                              backgroundColor: '#3AC9A7',
                              transform: 'scale(1.05)',
                              transition: 'all 0.2s ease'
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(0,0,0,0.5)'
                            }
                          }}
                        >
                          {generatingSongs ? 'Generating...' : 'Generate More Songs'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => navigate('/subscription')}
                          sx={{
                            color: 'black',
                            textTransform: 'none',
                            borderRadius: '20px',
                            padding: { xs: '8px 8px', sm: '8px 24px' },
                            marginLeft: { xs: '8px', sm: '8px' },
                            '&:hover': {
                              backgroundColor: '#3AC9A7',
                              transform: 'scale(1.05)',
                              transition: 'all 0.2s ease'
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(0,0,0,0.5)'
                            }
                          }}
                        >
                          Subscribe to generate new songs
                        </Button>
                      )}
                    </>
                  ) : null }
                </>
              )}

              {!isAuthenticated && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/login')}
                  sx={{
                    color: 'black',
                    textTransform: 'none',
                    borderRadius: '20px',
                    padding: '8px 24px',
                    '&:hover': {
                      backgroundColor: '#3AC9A7',
                      transform: 'scale(1.05)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                >
                  Sign in to Generate Songs
                </Button>
              )}

              {generationError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {generationError}
                </Alert>
              )}
            </div>
          </Box>

          <List>
            {playlist.songs.map((song, index) => (
              <React.Fragment key={song._id || index}>
                <ListItem 
                  alignItems="flex-start"
                  sx={{ 
                    cursor: song.audioUrl == null ? 'default' : 'pointer',
                    '&:hover': { 
                      backgroundColor: song.audioUrl == null ? 'transparent' : 'rgba(0, 0, 0, 0.04)' 
                    },
                    backgroundColor: currentSong && (currentSong._id === song._id || currentSong.audioUrl === song.audioUrl) ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                  }}
                  onClick={(e) => {
                    if (song.audioUrl == null) {
                      e.preventDefault();
                      return;
                    }
                    handlePlaySong(song);
                  }}
                >
                  <ListItemAvatar sx={{ pr: 2 }}>
                    <Avatar 
                      alt={song.title} 
                      src={song.imageUrl || null}
                      sx={{ width: 56, height: 56 }}
                    >
                      {!song.imageUrl && <MusicNoteIcon />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" component="span">
                          {song.title}
                        </Typography>
                        {song.instrumental && (
                          <Tooltip title="Instrumental (No Lyrics)">
                            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                              <GuitarIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                            </Box>
                          </Tooltip>
                        )}
                        {song.isGenerating && (
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                            <CircularProgress size={16} />
                          </Box>
                        )}
                        {!song.isGenerating && song.audioUrl == null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                            <Typography variant="body2" color="error">
                              Please try again later
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {formatDuration(song.duration)} {/* {song.artist || 'Unknown Artist'} • {formatDuration(song.duration)} */}
                      </Typography>
                    }
                  />
                  <IconButton 
                    edge="end" 
                    aria-label="play/pause"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentSong && (currentSong._id === song._id || currentSong.audioUrl === song.audioUrl)) {
                        // If the same song is clicked, toggle play/pause
                        if (isPlaying) {
                          audioRef.current.pause();
                          setIsPlaying(false);
                        } else {
                          audioRef.current.play();
                          setIsPlaying(true);
                        }
                      } else {
                        // If a different song is clicked, play it
                        handlePlaySong(song);
                      }
                    }}
                    sx={{
                      mr: 0
                    }}
                    disabled={song.audioUrl == null}
                  >
                    {currentSong && (currentSong._id === song._id || currentSong.audioUrl === song.audioUrl) && isPlaying ? (
                      <PauseIcon />
                    ) : (
                      <PlayArrowIcon />
                    )}
                  </IconButton>
                </ListItem>
                {index < playlist.songs.length - 1 && (
                  <Divider 
                    variant="inset" 
                    component="li" 
                    sx={{ 
                      ml: 9,  // 56px (avatar width) + 16px (left padding) + 16px (right padding)
                      // mr: 2    // Match the left margin to create alignment with text
                    }}
                  />
                )}
              </React.Fragment>
            ))}
            
            {/* Loading placeholder with pulse animation */}
            {/* {generatingSongs && (
              <React.Fragment>
                <Divider 
                  variant="inset" 
                  component="li" 
                  sx={{ 
                    ml: 9,
                    // mr: 2
                  }}
                />
                <ListItem alignItems="flex-start">
                  <ListItemAvatar sx={{ pr: 2 }}>
                    <Skeleton variant="circular" width={56} height={56} animation="pulse" />
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Skeleton variant="text" width="60%" animation="pulse" />}
                    secondary={<Skeleton variant="text" width="40%" animation="pulse" />}
                  />
                  <Skeleton variant="circular" width={40} height={40} animation="pulse" 
                    sx={{
                      mr: 0
                    }}
                  />
                </ListItem>
                <Divider 
                  variant="inset" 
                  component="li" 
                  sx={{ 
                    ml: 9,
                    // mr: 2
                  }}
                />
              </React.Fragment>
            )} */}
            
            {/* Error message if generation failed */}
            {/* {generationError && (
              <React.Fragment>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar sx={{ pr: 2 }}>
                    <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56 }}>
                      <Typography variant="body2" color="white">!</Typography>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Generation Failed"
                    secondary={
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {generationError}
                      </Alert>
                    }
                  />
                </ListItem>
                <Divider 
                  variant="inset" 
                  component="li" 
                  sx={{ 
                    ml: 9,
                    // mr: 2
                  }}
                />
              </React.Fragment>
            )} */}
          </List>
        </>
      ) : null}
    </Container>
  );
};

export default Playlist; 